import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Baby, ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "./ui/button";
import useReveal from "../hooks/useReveal";
import { CHILD_SEATS } from "../data/childseats";
import { CHILD_SEATS_I18N } from "../data/childSeatsI18n";
import { useLang } from "../i18n/LanguageContext";

export default function ChildSeats() {
  useReveal();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [idx, setIdx] = useState(0);
  const total = CHILD_SEATS.length;
  const loc = CHILD_SEATS_I18N[lang] || CHILD_SEATS_I18N.en;

  const go = useCallback((n) => setIdx((n + total) % total), [total]);

  useEffect(() => {
    const timer = setInterval(() => setIdx((i) => (i + 1) % total), 4500);
    return () => clearInterval(timer);
  }, [total]);

  const base = CHILD_SEATS[idx];
  const c = { ...base, ...(loc[base.id] || {}) };

  return (
    <section id="childseats" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="max-w-2xl reveal">
          <span className="text-xs uppercase tracking-[0.2em] text-[#F5B400] font-semibold flex items-center gap-2"><Baby className="w-4 h-4" /> {t("seats_eyebrow")}</span>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-bold text-black leading-tight">{t("seats_title")}</h2>
          <p className="mt-3 text-slate-500">{t("seats_sub")}</p>
        </div>

        <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center">
          <div className="relative reveal">
            <div className="rounded-3xl overflow-hidden bg-slate-50 border border-slate-100">
              <img key={c.id} src={c.image} alt={c.name} className="w-full h-[300px] md:h-[380px] object-contain p-6" data-testid="childseat-slide-image" />
            </div>
            <button onClick={() => go(idx - 1)} aria-label="Zurück" data-testid="childseat-prev" className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#F5B400] hover:text-neutral-900 transition-colors shadow-lg">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button onClick={() => go(idx + 1)} aria-label="Weiter" data-testid="childseat-next" className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white text-black flex items-center justify-center hover:bg-[#F5B400] hover:text-neutral-900 transition-colors shadow-lg">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="reveal">
            <div className="flex items-center justify-between gap-3">
              <h3 className="font-display text-3xl font-bold text-black" data-testid="childseat-slide-name">{c.name}</h3>
              <span className="font-display text-sm font-bold text-[#F5B400] text-right leading-snug" data-testid="childseat-price-note">{t("seats_price_note")}</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2 text-sm">
              <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">{c.ages}</span>
              <span className="rounded-full bg-slate-100 px-3 py-1.5 text-slate-600">{c.weight}</span>
            </div>
            <p className="mt-4 text-slate-500 text-lg leading-relaxed">{c.desc}</p>
            <div className="mt-8 flex items-center gap-5">
              <Button onClick={() => navigate("/booking")} className="bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 rounded-full px-8 h-12 font-semibold" data-testid={`childseat-book-${c.id}`}>
                {t("seats_add")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to={`/blog/${c.slug}`} className="inline-flex items-center gap-1.5 text-sm text-[#F5B400] font-semibold" data-testid={`childseat-blog-${c.id}`}>{t("seats_more")} <ArrowRight className="w-4 h-4" /></Link>
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {CHILD_SEATS.map((seat, i) => (
                <button key={seat.id} onClick={() => go(i)} data-testid={`childseat-dot-${seat.id}`}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${i === idx ? "bg-[#F5B400] text-neutral-900" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                  {(loc[seat.id] && loc[seat.id].name) || seat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
